import { z } from 'zod';

/**
 * Schemas Zod + tipos inferidos do contrato de leitura do Sales Admin
 * (`/admin/sales/*` no monolith). Status são uniões de string literais
 * (sem enum — `erasableSyntaxOnly`). Datas chegam como strings ISO no JSON.
 */

// --- Uniões de status (espelham os enums do monolith) ---

export const TransactionStatusSchema = z.enum([
  'paid',
  'refunded',
  'chargeback',
  'expired',
  'cancelled',
  'rejected',
  'failed',
  'delayed',
  'disputed',
  'billet_pending',
  // Cobrança emitida e sem pagamento, sem instrumento declarado — o
  // `billet_pending` diz "boleto impresso", este diz só "o gateway espera".
  'waiting_payment',
  'in_recovery',
  'blocked',
  'in_analysis',
  // Anulada por decisão admin (só lançamento manual) — não é `cancelled`, que
  // é eco do gateway, nem `refunded`, que implica dinheiro de volta.
  'voided',
]);
export type TransactionStatus = z.infer<typeof TransactionStatusSchema>;

/**
 * Por onde a transação entrou — espelha o `TransactionOrigin` do back.
 * `gateway_backfill` é a cobrança real que nunca chegou por webhook e foi
 * reconstruída do histórico da assinatura.
 */
export const TransactionOriginSchema = z.enum([
  'gateway_webhook',
  'gateway_backfill',
  'manual',
]);
export type TransactionOrigin = z.infer<typeof TransactionOriginSchema>;

export const PaymentMethodSchema = z.enum([
  'credit_card',
  'debit_card',
  'pix',
  'billet',
  'paypal',
  'free',
  'other',
]);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const InvoiceStatusSchema = z.enum([
  'pending',
  'overdue',
  'paid',
  'refunded',
  'cancelled',
  'replaced',
]);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const InvoiceOriginSchema = z.enum([
  'gateway_invoice',
  'synthesized',
  'manual',
]);

export type InvoiceOrigin = z.infer<typeof InvoiceOriginSchema>;

export const InvoiceSourceTypeSchema = z.enum(['contract', 'debt_arrangement']);

/**
 * Valor consolidado de um conjunto de faturas — o total somado das faturas, em
 * oposição a `BillingAgreement.totalPrice`, que só é o total de vida da BA
 * quando ela nasce de renegociação; vindo de webhook guarda o valor de **uma**
 * cobrança (Hotmart: uma parcela; Guru: um ciclo). Nunca use `totalPrice` como
 * total.
 *
 * As contagens existem para a tela justificar o número: total com ciclo
 * estimado ou faltando não deve se passar por exato.
 */
export const InvoiceValueSummarySchema = z.object({
  /** Soma dos ciclos ativos existentes; `null` quando nenhum valor é conhecido. */
  totalValue: z.number().nullable(),
  /** Valor conhecido mais recente de um ciclo — o que preenche os placeholders. */
  perCycleValue: z.number().nullable(),
  /** Ciclos ativos somados. */
  countedCycles: z.number(),
  /** Quantos dos somados usaram `perCycleValue` por não ter valor próprio. */
  estimatedCycles: z.number(),
  /**
   * Ciclos sem fatura nenhuma que ficam **antes** do último ciclo conhecido:
   * cobranças que já aconteceram e não chegaram até nós — anteriores à conexão
   * da conta de gateway, ou webhook perdido. É dado faltando, e é o que a tela
   * precisa destacar.
   */
  missingPastCycles: z.number(),
  /**
   * Ciclos declarados depois do último conhecido: ainda não foram cobrados.
   * Ausência esperada — o contrato só não chegou lá ainda.
   */
  missingFutureCycles: z.number(),
});
export type InvoiceValueSummary = z.infer<typeof InvoiceValueSummarySchema>;

/**
 * Consolidado de um contrato inteiro. Sem `perCycleValue`: com mais de uma BA no
 * histórico (renegociação troca o preço), "valor por ciclo do contrato" não
 * existe.
 */
export const ContractValueSummarySchema = InvoiceValueSummarySchema.omit({
  perCycleValue: true,
});
export type ContractValueSummary = z.infer<typeof ContractValueSummarySchema>;

export const ContractStatusSchema = z.enum([
  'pending_confirmation',
  'active',
  'overdue',
  'finished',
  'incompleted',
  'abandoned',
  'cancelled',
  'not_converted',
]);
export type ContractStatus = z.infer<typeof ContractStatusSchema>;

/**
 * Tenant (unidade de negócio) ao qual um contrato pertence — espelha
 * `Tenant` do monolith (`src/_shared/domain/tenant.ts`). Um contrato refere-se
 * sempre a exatamente um tenant; a conta do usuário é multi-tenant.
 */
export const TenantSchema = z.enum([
  'medq',
  'aristo',
  'cbi',
  'bbi',
  'territorio-saber',
  'faculdade-primum',
]);
export type Tenant = z.infer<typeof TenantSchema>;

export const BillingAgreementModeSchema = z.enum([
  'one_time',
  'subscription_fixed_cycles',
  'subscription',
]);
export type BillingAgreementMode = z.infer<typeof BillingAgreementModeSchema>;

export const BillingAgreementOriginSchema = z.enum([
  'gateway_webhook',
  'manual',
]);

export const BillingAgreementStatusSchema = z.enum([
  'waiting_payment',
  'processing',
  'trial',
  'inactive',
  'active',
  'pastdue',
  'cancelled',
  'finished',
  'failed',
  'not_converted',
]);
export type BillingAgreementStatus = z.infer<
  typeof BillingAgreementStatusSchema
>;

export const BillingAgreementIntervalUnitSchema = z.enum([
  'day',
  'week',
  'month',
  'year',
]);
export type BillingAgreementIntervalUnit = z.infer<
  typeof BillingAgreementIntervalUnitSchema
>;

export const DebtArrangementStatusSchema = z.enum([
  'draft',
  'waiting_payment',
  'active',
  'completed',
  'broken',
  'cancelled',
  'reverted',
]);
export type DebtArrangementStatus = z.infer<typeof DebtArrangementStatusSchema>;

export const DebtArrangementTypeSchema = z.enum(['overdue_plus_future']);

const ProviderMetadataSchema = z.record(z.string(), z.unknown()).nullable();

/**
 * Gateway de pagamento (`Provider` do monolith, `src/sales/domain/enums/provider.enum.ts`).
 * Distinto do `TotvsProvider` do módulo TOTVS — casados apenas por valor de string.
 */
export const ProviderSchema = z.enum([
  'guru',
  'hotmart',
  'eduzz',
  'apple',
  'google',
]);
export type Provider = z.infer<typeof ProviderSchema>;

/**
 * Cadastro da conta de gateway. **Sem `accountIdentifier`**: ele é o segredo
 * dos webhooks recebidos e saiu do retorno de `GET admin/sales/provider-accounts`,
 * que é gate de curinga administrativo. Não o traga de volta para exibir numa
 * tela.
 */
export const ProviderAccountSchema = z.object({
  id: z.string(),
  provider: ProviderSchema,
  displayName: z.string(),
  tenant: TenantSchema,
  notes: z.string().nullable(),
});
export type ProviderAccount = z.infer<typeof ProviderAccountSchema>;

// --- Webhook Inbox (log de webhooks recebidos, tela admin) ---

export const SaleWebhookInboxStatusSchema = z.enum([
  'pending',
  'processing',
  'processed',
  'ignored',
  'failed',
  // Não é terminal: subscription event cuja BillingAgreement ainda não existe
  // (assinatura legada, sem transaction event no monólito). Volta a `pending`
  // sozinha quando a transação daquela assinatura chega.
  'waiting_transaction',
]);
export type SaleWebhookInboxStatus = z.infer<
  typeof SaleWebhookInboxStatusSchema
>;

export const WebhookEventTypeSchema = z.enum(['transaction', 'subscription']);
export type WebhookEventType = z.infer<typeof WebhookEventTypeSchema>;

export const SaleWebhookInboxSchema = z.object({
  id: z.string(),
  providerAccountId: z.string(),
  eventType: WebhookEventTypeSchema,
  status: SaleWebhookInboxStatusSchema,
  // `string`, não enum: um código novo do back não pode derrubar o parse da
  // tela. Opcional porque a row anterior ao campo chega sem ele.
  statusReason: z.string().nullable().optional(),
  statusDetail: z.string().nullable().optional(),
  inThisStatusSince: z.string(),
  idempotenceSource: z.string(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SaleWebhookInbox = z.infer<typeof SaleWebhookInboxSchema>;

export const SaleWebhookInboxListResponseSchema = z.object({
  items: z.array(SaleWebhookInboxSchema),
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
});
export type SaleWebhookInboxListResponse = z.infer<
  typeof SaleWebhookInboxListResponseSchema
>;

export interface ForceProcessSaleWebhookInboxInput {
  reason?: string;
}

// --- Transação ---

export const TransactionListItemSchema = z.object({
  id: z.string(),
  externalTransactionId: z.string(),
  status: TransactionStatusSchema,
  amount: z.number(),
  installments: z.number(),
  origin: TransactionOriginSchema,
  paymentMethod: PaymentMethodSchema.nullable(),
  userId: z.string(),
  invoiceId: z.string(),
  providerAccountId: z.string(),
  tenant: TenantSchema.nullable(),
  productName: z.string().nullable(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  occurredAt: z.string(),
  /**
   * Quando soubemos que a venda está liquidada (garantia do gateway vencida).
   * É fato, não status: a transação continua `paid`. `.optional()` para o
   * front novo ler o back que ainda não manda o campo.
   */
  settledAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TransactionListItem = z.infer<typeof TransactionListItemSchema>;

export const TransactionDetailSchema = TransactionListItemSchema.extend({
  providerMetadata: ProviderMetadataSchema,
});
export type TransactionDetail = z.infer<typeof TransactionDetailSchema>;

export const TransactionsListResponseSchema = z.object({
  items: z.array(TransactionListItemSchema),
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
});
export type TransactionsListResponse = z.infer<
  typeof TransactionsListResponseSchema
>;

// --- Invoices ---

/**
 * De onde o back tirou a data de cobrança da fatura: `charged` é cobrança que
 * aconteteu, `estimated` é projeção pela periodicidade da assinatura, `closed` é
 * ciclo que terminou sem cobrança (cancelado/substituído) e `unknown` é "não dá
 * para dizer". A tela precisa da distinção — as quatro pedem frases diferentes.
 */
export const InvoiceChargeDateKindSchema = z.enum([
  'charged',
  'estimated',
  'closed',
  'unknown',
]);
export type InvoiceChargeDateKind = z.infer<typeof InvoiceChargeDateKindSchema>;

export const InvoiceSchema = z.object({
  id: z.string(),
  sourceId: z.string(),
  sourceType: InvoiceSourceTypeSchema,
  billingAgreementId: z.string(),
  value: z.number().nullable(),
  cycleIndex: z.number(),
  status: InvoiceStatusSchema,
  origin: InvoiceOriginSchema,
  externalId: z.string().nullable(),
  chargeDate: z.string().nullable(),
  chargeDateKind: InvoiceChargeDateKindSchema,
  /**
   * Link em que o aluno paga a fatura. O back só o manda com a fatura em aberto
   * e a assinatura viva — `null` no resto, inclusive na Hotmart.
   */
  paymentUrl: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

/**
 * Tentativa de cobrança de uma fatura — bloco "Transações desta fatura" do modal
 * "Resumo da Fatura". Lean de propósito (sem contato/produto/BU/metadata): o
 * `id` leva à página da transação, onde está o detalhe completo.
 */
export const InvoiceTransactionSchema = z.object({
  id: z.string(),
  externalTransactionId: z.string(),
  status: TransactionStatusSchema,
  amount: z.number(),
  installments: z.number(),
  origin: TransactionOriginSchema,
  paymentMethod: PaymentMethodSchema.nullable(),
  occurredAt: z.string(),
  settledAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type InvoiceTransaction = z.infer<typeof InvoiceTransactionSchema>;

export const TransactionInvoicesSchema = z.object({
  primaryInvoiceId: z.string(),
  billingAgreementId: z.string().nullable(),
  invoices: z.array(InvoiceSchema),
});
export type TransactionInvoices = z.infer<typeof TransactionInvoicesSchema>;

// --- Contract + usuário ---

export const UserSummarySchema = z.object({
  userId: z.string(),
  accountId: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  doc: z.string().nullable(),
});
export type UserSummary = z.infer<typeof UserSummarySchema>;

export const ContractSchema = z.object({
  id: z.string(),
  userId: z.string(),
  status: ContractStatusSchema,
  startDate: z.string(),
  endDate: z.string().nullable(),
  currentBillingAgreementId: z.string().nullable(),
  firstBillingAgreementId: z.string().nullable(),
  mergedIntoContractId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Contract = z.infer<typeof ContractSchema>;

export const TransactionContractSchema = z.object({
  contract: ContractSchema,
  user: UserSummarySchema.nullable(),
});
export type TransactionContract = z.infer<typeof TransactionContractSchema>;

// --- Billing Agreement ---

/**
 * Endereço do comprador como veio do checkout. Todo campo é nullable: o payload
 * de coprodutor da Hotmart não traz `address`, e nenhum dos dois gateways
 * garante os subcampos.
 */
export const BuyerContactAddressSchema = z.object({
  street: z.string().nullable(),
  number: z.string().nullable(),
  neighborhood: z.string().nullable(),
  complement: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string().nullable(),
  zipCode: z.string().nullable(),
});
export type BuyerContactAddress = z.infer<typeof BuyerContactAddressSchema>;

/**
 * O contato do comprador **como o gateway o entregou no checkout** — não o
 * cadastro atual, que vive no `User`, é sobrescrito pela compra seguinte e
 * reapontado pela troca de titularidade do contrato.
 *
 * As duas formas de vazio dizem coisas diferentes, e é delas que saem os três
 * estados da tela: `buyerContact` **inteiro** `null` = nunca capturamos (venda
 * anterior à captura, sem webhook guardado); campo interno `null` = o gateway
 * não mandou aquele dado (credencial de afiliado da Hotmart não recebe o
 * comprador; a de coprodutor não recebe o endereço).
 */
export const BuyerContactSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
  doc: z.string().nullable(),
  phone: z.string().nullable(),
  address: BuyerContactAddressSchema.nullable(),
  /** Instante do evento **no gateway** de onde este contato foi lido. */
  capturedAt: z.string(),
});
export type BuyerContact = z.infer<typeof BuyerContactSchema>;

export const BillingAgreementSchema = z.object({
  id: z.string(),
  contractId: z.string(),
  offerId: z.string(),
  providerAccountId: z.string(),
  externalId: z.string(),
  mode: BillingAgreementModeSchema,
  origin: BillingAgreementOriginSchema,
  status: BillingAgreementStatusSchema,
  cycleCount: z.number().nullable(),
  totalPrice: z.number().nullable(),
  chargeInterval: z.number().nullable(),
  chargeIntervalUnit: BillingAgreementIntervalUnitSchema.nullable(),
  replacesBillingAgreementId: z.string().nullable(),
  cancelReason: z.string().nullable(),
  cancelledByEmail: z.string().nullable(),
  cancelledByName: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  lastStatusAt: z.string().nullable(),
  canCancel: z.boolean(),
  /**
   * Snapshot provider-specific da assinatura. É onde mora o id que abre a tela
   * do gateway: no Guru, `subscriptionCode` (`sub_…`, o da URL do painel) — o
   * `externalId` acima é o `internal_id` (UUID), que não abre nada de lá.
   */
  providerMetadata: z.record(z.string(), z.unknown()).nullable(),
  /** Ver `BuyerContactSchema`: o que cada forma de `null` quer dizer. */
  buyerContact: BuyerContactSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BillingAgreement = z.infer<typeof BillingAgreementSchema>;

// --- Offer + Debt Arrangements ---

export const OfferSchema = z.object({
  id: z.string(),
  providerAccountId: z.string(),
  /** Gateway da conta que originou a oferta. `null` = conta não resolvida — nunca inferido pela forma do id. */
  provider: ProviderSchema.nullable(),
  /** Nome de exibição da conta de gateway. `null` se não resolvida. */
  providerAccountName: z.string().nullable(),
  /** BU dona da conta de gateway. `null` se não resolvida. */
  tenant: TenantSchema.nullable(),
  externalId: z.string(),
  /**
   * Id do produto no gateway, promovido do `providerMetadata` para campo
   * próprio — metade da chave externa que casa `Product`/`TotvsProductLink`.
   */
  externalProductId: z.string().nullable(),
  /** Nome da OFERTA no gateway (pode ter nome próprio, distinto do produto). */
  name: z.string(),
  /** Nome do PRODUTO — é o que deve ser exibido como "Produto"/"Nome do Produto". */
  productName: z.string().nullable(),
  sku: z.string().nullable(),
  /**
   * Snapshot provider-specific da oferta. É onde mora o id do PRODUTO no
   * gateway (`externalProductId`) — o `externalId` acima é da OFERTA, não do
   * produto.
   */
  providerMetadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Offer = z.infer<typeof OfferSchema>;

export const DebtArrangementOfferSnapshotSchema = z.object({
  cycles: z.number().nullable(),
  value: z.number().nullable(),
  currency: z.string().nullable(),
  interval: z.number().nullable(),
  intervalType: z.string().nullable(),
});

export const DebtArrangementSchema = z.object({
  id: z.string(),
  contractId: z.string(),
  replacedBillingAgreementId: z.string(),
  type: DebtArrangementTypeSchema,
  status: DebtArrangementStatusSchema,
  reason: z.string(),
  externalOfferId: z.string().nullable(),
  resultingBillingAgreementId: z.string().nullable(),
  offerSnapshot: DebtArrangementOfferSnapshotSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DebtArrangement = z.infer<typeof DebtArrangementSchema>;

/** DA com as BAs relacionadas (substituída + resultante) resolvidas — cadeia. */
export const DebtArrangementWithRelationsSchema = DebtArrangementSchema.extend({
  replacedBillingAgreement: BillingAgreementSchema.nullable(),
  resultingBillingAgreement: BillingAgreementSchema.nullable(),
  sourceInvoiceIds: z.array(z.string()),
});
export type DebtArrangementWithRelations = z.infer<
  typeof DebtArrangementWithRelationsSchema
>;

export const TransactionOfferAndArrangementsSchema = z.object({
  billingAgreementId: z.string().nullable(),
  offer: OfferSchema.nullable(),
  debtArrangements: z.array(DebtArrangementWithRelationsSchema),
});
export type TransactionOfferAndArrangements = z.infer<
  typeof TransactionOfferAndArrangementsSchema
>;

// --- Contratos (página de Contratos + detalhe por abas) ---

/**
 * Linha da lista de contratos. Estende o contrato cru com os campos resolvidos
 * server-side para exibição: produto (nome da Offer da BA atual), contato (nome
 * do usuário) e tenant (BU, via `BA.providerAccount.tenant`). `null` quando o
 * elo correspondente não existe.
 */
export const ContractListItemSchema = ContractSchema.extend({
  productName: z.string().nullable(),
  contactName: z.string().nullable(),
  tenant: TenantSchema.nullable(),
  valueSummary: ContractValueSummarySchema,
});
export type ContractListItem = z.infer<typeof ContractListItemSchema>;

export const ContractsListResponseSchema = z.object({
  items: z.array(ContractListItemSchema),
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
});
export type ContractsListResponse = z.infer<typeof ContractsListResponseSchema>;

/** Detalhe do contrato: contrato + usuário + BA atual + offer + tenant (BU). */
export const ContractDetailSchema = z.object({
  contract: ContractSchema,
  user: UserSummarySchema.nullable(),
  billingAgreement: BillingAgreementSchema.nullable(),
  offer: OfferSchema.nullable(),
  tenant: TenantSchema.nullable(),
});
export type ContractDetail = z.infer<typeof ContractDetailSchema>;

/**
 * Faturas **ativas** do contrato inteiro — de todas as BAs do histórico (atual +
 * substituídas por renegociação), já sem `replaced`/`cancelled`. É exatamente o
 * conjunto que compõe o "Valor Total do Contrato". Para o histórico completo de
 * uma BA (com substituídas/canceladas), ver `BillingAgreementDetailSchema`.
 */
export const ContractInvoicesSchema = z.object({
  billingAgreementIds: z.array(z.string()),
  invoices: z.array(InvoiceSchema),
  valueSummary: ContractValueSummarySchema,
  /**
   * Alguma BA do contrato tem o que buscar no gateway. É o que liga o botão de
   * sincronizar — ele não aparece fora disso (ver `SyncChargeHistoryModal`).
   */
  needsChargeHistorySync: z.boolean(),
});
export type ContractInvoices = z.infer<typeof ContractInvoicesSchema>;

/** Todas as BAs que já pertenceram ao contrato, da mais antiga para a mais nova. */
export const ContractBillingAgreementsSchema = z.object({
  billingAgreements: z.array(BillingAgreementSchema),
});
export type ContractBillingAgreements = z.infer<
  typeof ContractBillingAgreementsSchema
>;

export const BillingAgreementProviderAccountSchema = z.object({
  id: z.string(),
  provider: ProviderSchema,
  displayName: z.string(),
});
export type BillingAgreementProviderAccount = z.infer<
  typeof BillingAgreementProviderAccountSchema
>;

/**
 * Detalhe de uma BA (página `/billing-agreements/:id`). As `invoices` vêm **sem
 * filtro de status** — é aqui que se veem as substituídas/canceladas que a
 * tabela do contrato omite.
 */
export const BillingAgreementDetailSchema = z.object({
  billingAgreement: BillingAgreementSchema,
  /**
   * Gateway que gerencia a BA — é lá que a assinatura vive e é operada. `null`
   * só se a conta tiver saído do cadastro. Recorte de `ProviderAccount` sem o
   * `accountIdentifier` (segredo dos webhooks recebidos).
   */
  providerAccount: BillingAgreementProviderAccountSchema.nullable(),
  offer: OfferSchema.nullable(),
  invoices: z.array(InvoiceSchema),
  /**
   * Conta só as faturas **ativas**, embora `invoices` venha sem filtro:
   * substituída/cancelada aparece na tabela mas não entra na soma.
   */
  valueSummary: InvoiceValueSummarySchema,
  /**
   * Esta BA tem o que buscar no gateway — o sinal que o servidor calcula das
   * nossas próprias faturas e transações, sem consultar ninguém.
   *
   * **São dois motivos:** ciclo cobrado faltando no histórico, e transação cuja
   * identidade o servidor inventou (`backfill:…`) e ainda vale procurar. O
   * segundo é por que o campo não se chama mais `hasChargeHistoryDesync`.
   *
   * Não é o mesmo que `valueSummary.missingPastCycles`: aquele conta ciclo **sem
   * fatura nenhuma**, e o caso mais comum aqui é a fatura placeholder que existe
   * e nunca recebeu a cobrança — invisível para aquela contagem.
   */
  needsChargeHistorySync: z.boolean(),
});
export type BillingAgreementDetail = z.infer<
  typeof BillingAgreementDetailSchema
>;

// --- Eventos (audit log) da transação ---

export const TransactionEventSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  actorPlatformProfileId: z.string().nullable(),
  /**
   * Ator resolvido pelo back. `.optional()` para que front novo continue lendo
   * back antigo — o formato é o que o `actorName` do sales já usava.
   */
  actorName: z.string().nullable().optional(),
  /** Pessoa por trás do perfil do ator, para linkar `/management/users/:userId`. */
  actorUserId: z.string().nullable().optional(),
  /**
   * Humano real por trás de uma ação feita sob act-as. Fora de máscara vem
   * `null` — e `actorName` acima continua sendo o perfil **mascarado**.
   */
  realActorName: z.string().nullable().optional(),
  realActorUserId: z.string().nullable().optional(),
  reason: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  correlationId: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type TransactionEvent = z.infer<typeof TransactionEventSchema>;

/** Entidades auditáveis do Sales (espelha `SalesEntityType` do monolith). */
export type SalesEntityType =
  | 'Contract'
  | 'BillingAgreement'
  | 'Invoice'
  | 'DebtArrangement'
  | 'Transaction'
  | 'SaleWebhookInbox';

export const TransactionEventsResponseSchema = z.object({
  items: z.array(TransactionEventSchema),
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
});
export type TransactionEventsResponse = z.infer<
  typeof TransactionEventsResponseSchema
>;

/**
 * Evento listado por `correlationId` (tudo que uma row do webhook inbox gerou):
 * traz a entidade porque a mesma row mexe em mais de uma.
 */
export const SalesCorrelatedEntityEventSchema = TransactionEventSchema.extend({
  entityType: z.string(),
  entityId: z.string(),
});
export type SalesCorrelatedEntityEvent = z.infer<
  typeof SalesCorrelatedEntityEventSchema
>;

export const SalesCorrelatedEntityEventsResponseSchema =
  TransactionEventsResponseSchema.extend({
    items: z.array(SalesCorrelatedEntityEventSchema),
  });
export type SalesCorrelatedEntityEventsResponse = z.infer<
  typeof SalesCorrelatedEntityEventsResponseSchema
>;

// --- Alertas operacionais do Sales (Central de Pendências) ---

/**
 * Status do alerta — **maiúsculo**, `OperationalAlertStatus` do shared do
 * monolith (`src/_shared/alerts/`). O feed agregado da Central é minúsculo e
 * converte em `salesAlertsPendingSource`.
 */
export type SalesOperationalAlertStatus = 'OPEN' | 'RESOLVED' | 'DISMISSED';

/**
 * Formato HTTP de um `OperationalAlert` do Sales — espelha
 * `SalesOperationalAlertOutputDto` do monolith. `type`/`entityType` chegam
 * como string livre: o genérico do shared não conhece o enum do módulo, e o
 * back pode ganhar um tipo novo antes de esta tela aprender o nome dele (ver
 * os mapas de rótulo em `presentation/alerts-pending-source.ts`).
 *
 * **`details` é onde mora a fila, em um dos tipos.** No
 * `pending_invoices_beyond_limit` não existe coleção para onde mandar o
 * operador — a lista de faturas paradas vive dentro do próprio alerta
 * (`details.cases`), e é ela que a Central de Pendências abre no painel da
 * linha. Ver `SalesAlertType` no monolith.
 */
export interface SalesOperationalAlert {
  id: string;
  type: string;
  entityType: string;
  entityId: string;
  status: SalesOperationalAlertStatus;
  inThisStatusSince: string;
  details: Record<string, unknown>;
  resolutionNote: string | null;
  resolvedByPlatformProfileId: string | null;
  resolutionAttachmentFileId: string | null;
  /**
   * Quando quem fechou marcou "não abrir de novo para esta entidade" — só em
   * tipo de dedupe por entidade (`duplicate_payment`). Enquanto houver, a
   * próxima detecção da mesma fatura não abre alerta; limpa-se pela
   * `reopen-for-entity`, com justificativa.
   */
  closedForEntityAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * `details` do `duplicate_payment` — os campos que o back grava. Tudo opcional
 * porque `details` é `Record<string, unknown>` no contrato: a tela lê o que
 * existir e não quebra se o detector passar a gravar outro shape.
 * `duplicateTransactionIds` cresce enquanto o alerta está aberto: outro
 * pagamento duplicado na mesma fatura entra na lista em vez de abrir outro.
 */
export interface DuplicatePaymentDetails {
  contractId?: string;
  billingAgreementId?: string;
  duplicateTransactionIds?: string[];
  previousPaidTransactionIds?: string[];
}

/**
 * `details.cases` do `pending_invoices_beyond_limit` — espelha
 * `PendingInvoiceBeyondLimit` (`application/invoice-overdue-limit.ts`). Tudo
 * opcional porque `details` é `Record<string, unknown>` no contrato: a tela lê
 * o que existir e não quebra se o detector passar a gravar outro shape.
 */
export interface PendingInvoiceBeyondLimitCase {
  invoiceId?: string;
  billingAgreementId?: string;
  cycleIndex?: number;
  /** `YYYY-MM-DD` — fim do período cobrado (ou o começo, quando só ele veio). */
  periodEndsAt?: string;
}

export interface ListSalesOperationalAlertsInput {
  page?: number;
  perPage?: number;
  status?: SalesOperationalAlertStatus;
  type?: string;
  entityType?: string;
  entityId?: string;
  /** Só os fechados com "não abrir de novo para esta entidade". */
  closedForEntity?: boolean;
  /** `asc` = a mais antiga primeiro (a ordem da Central de Pendências). Default do back: `desc`. */
  order?: 'asc' | 'desc';
}

/** Uma opção da faceta de tipo: o `type` cru do back e quantos alertas há dele. */
export interface OperationalAlertFacet {
  value: string;
  count: number;
}

export interface SalesOperationalAlertsListResponse {
  items: SalesOperationalAlert[];
  total: number;
  page: number;
  perPage: number;
}

/**
 * `entityType` do alerta no histórico de auditoria
 * (`GET /admin/sales/entities/:entityType/:id/events`) — espelha
 * `SalesEntityType.OPERATIONAL_ALERT` do monolith. O back lê junto, pelo mesmo
 * id, os eventos de quando o pagamento duplicado era `SalesInconsistency`.
 */
export const SALES_OPERATIONAL_ALERT_ENTITY_TYPE = 'OperationalAlert';

/** O que o endpoint de auditoria aceita: as entidades do Sales mais o alerta. */
export type SalesAuditEntityType =
  | SalesEntityType
  | typeof SALES_OPERATIONAL_ALERT_ENTITY_TYPE;

// --- Inputs/Results das operações com justificativa ---

/**
 * Corpo comum das operações que **exigem justificativa** (fechar alerta,
 * anular lançamento). O anexo viaja como `fileId` — foi enviado antes por
 * `POST /admin/sales/attachments`, cujo corpo é o arquivo cru.
 */
export interface JustifiedActionInput {
  note: string;
  attachmentFileId?: string;
}

/** Fechar um alerta; `closeForEntity` só vale em tipo de dedupe por entidade. */
export interface CloseOperationalAlertInput extends JustifiedActionInput {
  closeForEntity?: boolean;
}

export interface ManuallySettleInvoiceInput extends JustifiedActionInput {
  /** Valor recebido. Ausente → o valor da própria fatura. */
  amount?: number;
  /** Quando o pagamento aconteceu de fato (ISO). Ausente → agora. */
  paidAt?: string;
  paymentMethod?: PaymentMethod;
}

export interface ManuallySettleInvoiceResult {
  invoiceId: string;
  transactionId: string;
}

export interface InvalidateManualTransactionResult {
  transactionId: string;
  invoiceId: string;
  /** Status para o qual a fatura voltou, ou `null` se seguiu paga. */
  invoiceRevertedTo: InvoiceStatus | null;
  /** Status para o qual o contrato foi recomputado, ou `null` se não mudou. */
  contractRevertedTo: ContractStatus | null;
}

export interface UploadAttachmentResult {
  fileId: string;
}

// --- Inputs/Results das ações de BA e DA ---

export interface CancelBillingAgreementInput {
  reason?: string;
  comment?: string;
}

export type CancelBillingAgreementResult =
  | { outcome: 'CANCEL_REQUESTED' }
  | {
      outcome: 'MANUAL_CANCELLATION_REQUIRED';
      subscriptionCode: string | null;
    };

export interface ResolveCycleCountInput {
  reason?: string;
}

/**
 * Desfecho da resolução do total de ciclos da assinatura pela API do gateway
 * (T2). Só é ofertada para BA `subscription` com `cycleCount: null`.
 */
export interface ResolveCycleCountResult {
  billingAgreementId: string;
  outcome: /** Gateway confirmou assinatura sem fim: `cycleCount = 0`. */
    | 'marked_open'
    /** Gateway devolveu N ciclos: BA promovida a `subscription_fixed_cycles`. */
    | 'marked_fixed'
    /** N <= ciclo atual: nada aplicado, caso para investigar. */
    | 'anomaly_ignored'
    /** BA cancelada/encerrada/falha: gateway nem consultado, nada aplicado. */
    | 'skipped_terminal_status'
    /**
     * Gateway respondeu que não conhece assinatura com esse id — não é falha:
     * o caso conhecido é o parcelamento inteligente da Hotmart, que recorre sem
     * ser assinatura e portanto não tem ciclos a resolver.
     */
    | 'gateway_has_no_subscription';
  /**
   * `null` quando não houve número: `skipped_terminal_status` (gateway não
   * consultado) e `gateway_has_no_subscription` (sem assinatura lá).
   */
  resolvedCycles: number | null;
  /** `null` em `skipped_terminal_status` — a invoice nem é buscada. */
  currentCycleIndex: number | null;
  createdInvoiceCycleIndexes: number[];
}

export interface SyncChargeHistoryInput {
  reason?: string;
}

/** Por que uma cobrança do gateway não virou fatura aqui. */
export type SkippedGatewayChargeReason =
  /** Sem id: não dá para casar nem para garantir idempotência. */
  | 'unidentified'
  /** O gateway não diz em que ciclo ela caiu. */
  | 'no_cycle_number'
  /** O status que o gateway reporta não é um que saibamos traduzir. */
  | 'unreadable_status'
  /** Sem valor ou sem data — reconstruí-la seria inventar o número. */
  | 'incomplete'
  /**
   * O ciclo que o gateway carimbou nela já tem outra cobrança dele — o número
   * de ciclo do gateway não é único, e a fatura daqui é por ciclo.
   *
   * Some quando a assinatura converte para a numeração por período: a partir
   * daí quem decide é `period_already_charged`.
   */
  | 'cycle_already_charged'
  /**
   * O período que ela cobre já tem cobrança do gateway aqui. É o
   * `cycle_already_charged` depois da conversão, e não deveria acontecer — o
   * Guru não repete `period_start` na mesma assinatura. Se aparecer numa tela,
   * é caso para o time olhar, não para o operador decidir.
   */
  | 'period_already_charged';

export interface SkippedGatewayCharge {
  externalId: string | null;
  gatewayCycleNumber: number | null;
  /** O status cru do provedor — é o que se pede ao operador num `unreadable_status`. */
  gatewayStatus: string | null;
  reason: SkippedGatewayChargeReason;
}

/**
 * Desfecho da reconciliação de uma BA. `no_sync_needed` é resposta comum e não
 * é falha: o recheque local roda **depois** do clique, e o gatilho automático
 * da ingestão pode ter consertado a BA no meio do caminho.
 *
 * `substitutes_promoted` é o desfecho em que nenhuma cobrança nasceu e a BA
 * mudou assim mesmo: uma transação que carregava identidade inventada adotou a
 * do gateway. A tabela não ganha linha — o id da transação é que passa a ser o
 * que se encontra no painel do provedor.
 */
export interface SyncChargeHistoryResult {
  billingAgreementId: string;
  outcome:
    | 'no_sync_needed'
    | 'nothing_to_sync'
    | 'substitutes_promoted'
    | 'synced';
  /**
   * Faturas que passaram a existir (ou a valer) — de qualquer estado: a
   * cobrança atrasada ou estornada que o gateway teve também entra.
   */
  backfilledInvoiceIds: string[];
  skipped: SkippedGatewayCharge[];
}

/** Uma linha por BA de assinatura do contrato, na ordem do histórico. */
export interface SyncContractChargeHistoryResult {
  contractId: string;
  billingAgreements: SyncChargeHistoryResult[];
}

export interface CreateDebtArrangementInput {
  contractId: string;
  replacedBillingAgreementId: string;
  type: 'overdue_plus_future';
  invoiceIds: string[];
  reason: string;
}

export interface ActivateDebtArrangementInput {
  externalOfferId: string;
  externalProductId?: string;
}

export interface UpdateDebtArrangementDraftInput {
  invoiceIds: string[];
  reason: string;
}

export interface DebtArrangementReasonInput {
  reason?: string;
}

export interface AbandonContractInput {
  reason?: string;
}

export interface AbandonContractResult {
  contractId: string;
  status: ContractStatus;
  endDate: string | null;
}
